import React, { useEffect, useRef, useState } from 'react';
import PlusIcon from '../../assets/icons/Plus-b.svg';
import { useFormikContext } from 'formik';
import { putPhoto, putStatic } from '../../api/fileStorage';
import './UploadPhoto.scss';
import { useSelector } from 'react-redux';
import { DoctorsPageState } from '../../store/doctorsSlice';
import Resizer from 'react-image-file-resizer';
import { getFullImageUrl } from '../../utils/constants';

type Props = {
  onPhotoChange: (base64String: string) => void;
  putToStaticFolder?: boolean;
  defaultPhoto?: string;
  isDisable?: boolean;
};

const UploadPhoto = ({
  onPhotoChange,
  putToStaticFolder = false,
  defaultPhoto,
  isDisable = false,
}: Props) => {
  const [photo, setPhoto] = useState<string | null>(null);
  const [sizeError, setSizeError] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { setFieldValue } = useFormikContext();
  const { doctorDataToEdit } = useSelector(
    (state: { doctors: DoctorsPageState }) => state.doctors
  );

  const handleContainerClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  useEffect(() => {
    if (doctorDataToEdit && doctorDataToEdit.photo_url)
      setPhoto(doctorDataToEdit.photo_url);
    if (defaultPhoto) {
      // Use getFullImageUrl to properly construct the image URL
      const fullImageUrl = getFullImageUrl(defaultPhoto);
      setPhoto(fullImageUrl);
    }
  }, [doctorDataToEdit, defaultPhoto]);

const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  const file = event.target.files?.[0];
  if (!file) return;

  if (file.size > 5242880) {
    setSizeError(true);
    setUploadError('File size exceeds 5MB limit');
    return;
  } else {
    setSizeError(false);
    setUploadError(null);
  }

  const extension = file.type.split('/')[1]?.toUpperCase(); // 'JPEG', 'PNG', etc.
  const format = extension === 'HEIC' ? 'JPEG' : extension;

  setIsUploading(true);
  setUploadError(null);

  Resizer.imageFileResizer(
    file,
    800, // width
    600, // height
    format,
    90,  // quality
    0,   // rotation
    (resizedFile) => {
      const resizedImageFile = new File([resizedFile as Blob], file.name, {
        type: (resizedFile as Blob).type,
      });

      // Wrap async logic in a separate function
      handleUpload(resizedImageFile, file);
    },
    'blob'
  );
};

// helper function
const handleUpload = async (resizedImageFile: File, originalFile: File) => {
  try {
    const putPhotoResp = putToStaticFolder
      ? await putStatic({ file: resizedImageFile })
      : await putPhoto({ file: resizedImageFile });

    if (!putPhotoResp || !putPhotoResp.full_key) {
      throw new Error('Upload failed: No response from server');
    }

    setFieldValue('photo', putPhotoResp.full_key);

    // Call onPhotoChange with the static path
    onPhotoChange(putPhotoResp.full_key);

    // For UI display, construct the full URL for the uploaded image
    const displayUrl = getFullImageUrl(putPhotoResp.full_key);
    setPhoto(displayUrl);
    setFieldValue('photo_url', displayUrl);

    // Also create a base64 preview for immediate display
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Use base64 for immediate preview, but keep the S3 URL as the main photo
      setPhoto(result);
    };
    reader.readAsDataURL(resizedImageFile);

    console.log('Image uploaded successfully:', {
      fullKey: putPhotoResp.full_key,
      displayUrl: displayUrl,
      putToStaticFolder: putToStaticFolder
    });

  } catch (error) {
    console.error('Upload error:', error);
    setUploadError('Failed to upload image. Please try again.');
    setPhoto(null);
  } finally {
    setIsUploading(false);
  }
};

  return (
    <div
      className='upload-doctor-photo-container'
      onClick={handleContainerClick}>
      <input
        ref={fileInputRef}
        type='file'
        accept='image/png, image/jpeg, image/jpg, image/heic'
        style={{ display: 'none' }}
        onChange={handleFileChange}
        disabled={isDisable}
      />
      {!photo ? (
        <div className='empty-photo'>
          <img src={PlusIcon} alt='plus' />
        </div>
      ) : (
        <div className='photo'>
          <img src={photo} className='uploaded-photo' alt='Uploaded Preview' />
        </div>
      )}
      <div className='image-info'>
        <span>Photo</span>
        <span className='image-size-info'>
          JPG, PNG, or HEIC. Max size of 5MB
        </span>
        {sizeError && (
          <span className='image-size-error'>
            Image exceeds 5MB. Upload a smaller one.
          </span>
        )}
        {uploadError && (
          <span className='image-size-error'>
            {uploadError}
          </span>
        )}
        {isUploading && (
          <span className='image-uploading'>
            Uploading...
          </span>
        )}
      </div>
    </div>
  );
};

export default UploadPhoto;
